// src/pages/Contact.js

import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Mail, MessageCircle } from "lucide-react";

export default function Contact() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <main className="bg-[#fafbff]">
        {/* Header */}
        <section className="border-b border-[#e5e7eb] bg-white">
          <div className="mx-auto max-w-[1200px] px-5 py-14 sm:px-6 lg:px-8 lg:py-16">
            <div className="max-w-[720px]">
              <p className="mb-3 text-[14px] font-bold uppercase tracking-[0.08em] text-[#0758b5]">
                Get In Touch
              </p>

              <h1 className="text-[38px] font-extrabold leading-[1.15] tracking-[-1px] text-[#10203b] sm:text-[44px]">
                Contact Us
              </h1>

              <p className="mt-4 text-[17px] leading-[1.65] text-[#64748b]">
                Have a question, need support, or want to connect with the
                BizNep team? We’re here to help.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Content */}
        <section className="py-12 sm:py-14 lg:py-16">
          <div className="mx-auto max-w-[1200px] px-5 sm:px-6 lg:px-8">
            <div className="grid gap-8 lg:grid-cols-[1fr_1.25fr]">
              {/* Left Side */}
              <div className="rounded-[18px] bg-[#eef7ff] p-7 sm:p-9">
                <h2 className="text-[28px] font-extrabold tracking-[-0.6px] text-[#10203b]">
                  We’d love to hear from you
                </h2>

                <p className="mt-4 text-[16px] leading-[1.7] text-[#50627d]">
                  Whether you’re looking for help using BizNep, have a
                  question about a business listing, or want to work with us,
                  feel free to get in touch.
                </p>

                <div className="mt-8 space-y-4">
                  {/* Support */}
                  <div className="rounded-[12px] bg-white px-5 py-4">
                    <div className="flex items-start gap-3">
                      <MessageCircle
                        size={22}
                        className="mt-0.5 shrink-0 text-[#0758b5]"
                      />

                      <div>
                        <p className="text-[13px] font-semibold text-[#64748b]">
                          Support
                        </p>

                        <p className="mt-1 text-[16px] font-semibold text-[#10203b]">
                          Need help with BizNep?
                        </p>

                        <p className="mt-1 text-[14px] leading-[1.5] text-[#64748b]">
                          We’re here to help with your questions and concerns.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Business Owners */}
                  <div className="rounded-[12px] bg-white px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#e8f1ff] text-[12px] font-bold text-[#0758b5]">
                        B
                      </div>

                      <div>
                        <p className="text-[13px] font-semibold text-[#64748b]">
                          Business Owners
                        </p>

                        <p className="mt-1 text-[16px] font-semibold text-[#10203b]">
                          Need help with your business listing?
                        </p>

                        <p className="mt-1 text-[14px] leading-[1.5] text-[#64748b]">
                          Contact us for questions about listing and managing
                          your business on BizNep.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Community */}
                  <div className="rounded-[12px] bg-white px-5 py-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#e8f8ef] text-[12px] font-bold text-[#16a05d]">
                        C
                      </div>

                      <div>
                        <p className="text-[13px] font-semibold text-[#64748b]">
                          Community
                        </p>

                        <p className="mt-1 text-[16px] font-semibold text-[#10203b]">
                          Let’s build a stronger local community together.
                        </p>

                        <p className="mt-1 text-[14px] leading-[1.5] text-[#64748b]">
                          BizNep helps people discover and support local
                          businesses.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side */}
              <div className="rounded-[18px] border border-[#e1e7ee] bg-white p-7 shadow-[0_4px_16px_rgba(40,70,100,0.05)] sm:p-9">
                <h2 className="text-[26px] font-extrabold text-[#10203b]">
                  Get in touch
                </h2>

                <p className="mt-3 text-[16px] leading-[1.65] text-[#64748b]">
                  For questions, support, or general enquiries, you can reach
                  the BizNep team by email.
                </p>

                {/* Email */}
                <a
                  href="mailto:support@biznep.net"
                  className="mt-7 flex items-center gap-4 rounded-[12px] border border-[#e1e7ee] bg-[#fafbff] p-5 transition-colors hover:bg-[#f5f9ff]"
                >
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#eef5ff]">
                    <Mail size={22} className="text-[#0758b5]" />
                  </div>

                  <div className="min-w-0">
                    <p className="text-[13px] font-semibold text-[#64748b]">
                      Email
                    </p>

                    <p className="mt-1 truncate text-[16px] font-semibold text-[#0758b5]">
                      support@biznep.net
                    </p>
                  </div>
                </a>

                {/* Support Message */}
                <div className="mt-5 rounded-[12px] bg-[#eef7ff] p-5">
                  <h3 className="text-[18px] font-bold text-[#10203b]">
                    We’re here to help
                  </h3>

                  <p className="mt-2 text-[15px] leading-[1.65] text-[#50627d]">
                    If you have questions about BizNep, business listings, or
                    using the platform, please get in touch with our team.
                  </p>
                </div>

                {/* Social Media */}
                <div className="mt-8 border-t border-[#edf1f5] pt-7">
                  <p className="text-center text-[14px] font-semibold text-[#64748b]">
                    Connect with BizNep
                  </p>

                  <div className="mt-4 flex justify-center gap-4">
                    <button
                      type="button"
                      aria-label="Facebook"
                      className="flex h-12 w-12 items-center justify-center rounded-full border border-[#e1e7ee] bg-white text-[20px] font-bold text-[#1877F2] transition-colors hover:bg-[#eef5ff]"
                    >
                      f
                    </button>

                    <button
                      type="button"
                      aria-label="Instagram"
                      className="flex h-12 w-12 items-center justify-center rounded-full border border-[#e1e7ee] bg-white text-[20px] font-bold text-[#E4405F] transition-colors hover:bg-[#fff2f5]"
                    >
                      ◎
                    </button>

                    <button
                      type="button"
                      aria-label="LinkedIn"
                      className="flex h-12 w-12 items-center justify-center rounded-full border border-[#e1e7ee] bg-white text-[18px] font-bold text-[#0A66C2] transition-colors hover:bg-[#eef6ff]"
                    >
                      in
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ CTA */}
            <div className="mt-8 rounded-[18px] border border-[#dce8f5] bg-white px-6 py-7 text-center sm:px-10">
              <h2 className="text-[23px] font-extrabold text-[#10203b]">
                Looking for quick answers?
              </h2>

              <p className="mx-auto mt-2 max-w-[620px] text-[15px] leading-[1.6] text-[#64748b]">
                Check our frequently asked questions for information about
                using BizNep, business listings, and account support.
              </p>

              <a
                href="/faq"
                className="mt-5 inline-flex items-center rounded-[8px] bg-[#0866c9] px-6 py-3 text-[15px] font-bold text-white transition-colors hover:bg-[#075bb5]"
              >
                Visit FAQs
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}